/**
 * Normalizes a `SupportTicket` (from the dedicated support-ticket endpoints)
 * into the ticket shape the shared list / table / board / map views consume.
 *
 * The views were originally built against the Work Order `Ticket` shape, so we
 * expose Work-Order-style aliases (`ticketId`, `ticketType`, `facility`,
 * `machine`) alongside the native support-ticket fields. Work-Order-only fields
 * that have no support-ticket equivalent (`dueDate`, `schedule`, `archived`)
 * resolve to `undefined`, which every view already guards against.
 */

interface SupportTicketLike {
  _id: string;
  supportTicketId?: string;
  title?: string;
  description?: string;
  status?: string;
  type?: Record<string, unknown> | null;
  company?: Record<string, unknown> | null;
  assets?: Array<Record<string, any>> | null;
  assignees?: Array<Record<string, unknown>> | null;
  teams?: Array<Record<string, unknown>> | null;
  isCreatorInternal?: boolean;
  isMyTicket?: boolean;
  createdAt?: string;
  lastUpdatedAt?: string;
  createdBy?: Record<string, unknown> | null;
  supportTicketChatChannels?: string[];
  supportTicketInternalNotesChatChannels?: string[];
  [key: string]: unknown;
}

export const transformSupportTicket = (
  ticket?: SupportTicketLike | null,
): any => {
  if (!ticket) return null;

  const firstAsset = ticket.assets?.[0] ?? null;

  return {
    ...ticket,
    // Work-Order-style aliases used by the shared views.
    ticketId: ticket.supportTicketId,
    ticketType: ticket.type,
    facility: ticket.company,
    machine: firstAsset,
    ticketChatChannels: ticket.supportTicketChatChannels,
    ticketInternalNotesChatChannels:
      ticket.supportTicketInternalNotesChatChannels,
  };
};

export const transformSupportTickets = (
  tickets: Array<SupportTicketLike | null> = [],
): any[] =>
  tickets.map((ticket) => transformSupportTicket(ticket)).filter(Boolean);
